import { parseMaybeJson } from 'core/utils/json.utils';

type AnswerEntry = Record<string, unknown>;

type FrozenQuestion = { data?: unknown };

export type RevisionQuizQuestionData = AnswerEntry & {
  answer: unknown;
  question: unknown;
  questionGuid: string;
};

export type RevisionLearningObjectives = {
  cognitivePhase: string;
  microhability: string;
  nanohability: string;
};

type BuildParams = {
  answerEntry: unknown;
  frozenQuestion: FrozenQuestion | null | undefined;
  questionGuid: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isMissing(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Question data for the revision tutor. useQuizz only stores in metadata.answers the questions the student touched:
 * blank ones have no entry, are stored as null or lack `question`. For those, the question comes from
 * freezed_assessment_data with `answer: null`. Returns null when there is no question to send.
 */
export function buildRevisionQuizQuestionData({ answerEntry, frozenQuestion, questionGuid }: BuildParams): RevisionQuizQuestionData | null {
  const entry: AnswerEntry = isRecord(answerEntry) ? answerEntry : {};
  const question = isMissing(entry.question) ? parseMaybeJson(frozenQuestion?.data) : parseMaybeJson(entry.question);
  if (isMissing(question)) return null;

  return {
    ...entry,
    questionGuid: typeof entry.questionGuid === 'string' ? entry.questionGuid : questionGuid,
    question,
    answer: parseMaybeJson(entry.answer) ?? null,
  };
}

/**
 * Only the first objective is sent: its parent holds "cognitive phase - microhability" and it is the nanohability.
 */
export function mapRevisionLearningObjectives(learningObjectives: unknown): RevisionLearningObjectives {
  const main = Array.isArray(learningObjectives) && isRecord(learningObjectives[0]) ? learningObjectives[0] : {};
  const parent = isRecord(main.parent) ? main.parent : {};
  const [cognitivePhase = '', microhability = ''] = typeof parent.learning_objective === 'string' ? parent.learning_objective.split(' - ') : [];

  return {
    cognitivePhase,
    microhability,
    nanohability: typeof main.learning_objective === 'string' ? main.learning_objective : '',
  };
}
